/**
 * API Service Layer
 * Handles all HTTP requests to backend
 */

import axios from 'axios';

// Ensure baseURL always points to the backend API prefix (/api)
const RAW_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const API_URL = `${RAW_BASE.replace(/\/+$/, '')}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const warPartner = (() => { try { return JSON.parse(localStorage.getItem('user'))?.role === 'Partner'; } catch { return false; } })();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = warPartner ? '/login?bereich=partner' : '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getCurrentUser: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (email, otp, newPassword) => api.post('/auth/reset-password', { email, otp, newPassword })
};

// Booking API
export const bookingAPI = {
  getSlots: (startDate, endDate, studioId) => api.get('/slots', { params: { startDate, endDate, studioId } }),
  getBookings: (status) => api.get('/bookings', { params: { status } }),
  getMyBookings: () => api.get('/bookings'),
  getAllBookings: (startDate, endDate, studioId) => api.get('/bookings/all', { params: { startDate, endDate, studioId } }),
  createBooking: (slotId, persons) => api.post('/bookings', { slotId, persons }),
  cancelBooking: (bookingId) => api.delete(`/bookings/${bookingId}`)
};

// Partnerbereich (Physios) - eigener Login, eigene Routen
export const partnerAPI = {
  login: (email, password) => api.post('/partner/auth/login', { email, password }),
  forgotPassword: (email) => api.post('/partner/auth/forgot-password', { email }),
  resetPassword: (email, otp, newPassword) => api.post('/partner/auth/reset-password', { email, otp, newPassword }),
  me: () => api.get('/partner/me'),
  updateProfil: (data) => api.put('/partner/profil', data),
  getStudios: () => api.get('/partner/studios'),
  getSlots: (studioId, von, bis) => api.get('/partner/slots', { params: { studioId, von, bis } }),
  getEinschulungen: () => api.get('/partner/einschulungen'),
  buchen: (data) => api.post('/partner/einschulungen', data),
  stornieren: (id) => api.delete(`/partner/einschulungen/${id}`)
};

// Verwaltung der Partnerzugaenge (Owner)
export const partnerAdminAPI = {
  getAll: () => api.get('/owner/partner'),
  create: (data) => api.post('/owner/partner', data),
  update: (id, data) => api.put(`/owner/partner/${id}`, data),
  zugangSenden: (id) => api.post(`/owner/partner/${id}/zugang`),
  getEinschulungen: () => api.get('/owner/partner-einschulungen')
};

// Settings API (public, non-admin)
export const settingsAPI = {
  getPublicSettings: () => api.get('/settings/public')
};

// Studio API (admin)
export const studioAPI = {
  getAll: () => api.get('/admin/studios'),
  create: (data) => api.post('/admin/studios', data),
  update: (id, data) => api.put(`/admin/studios/${id}`, data),
  delete: (id) => api.delete(`/admin/studios/${id}`),
  getSchedules: (id) => api.get(`/admin/studios/${id}/schedules`),
  updateSchedules: (id, schedules) => api.put(`/admin/studios/${id}/schedules`, { schedules })
};

// Admin API
export const adminAPI = {
  getAllBookings: () => api.get('/admin/bookings'),
  getAllClients: () => api.get('/admin/clients'),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (settings) => api.put('/admin/settings', settings),
  createSlot: (slotData) => api.post('/admin/slots', slotData),
  getDashboardStats: () => api.get('/admin/dashboard'),

  createClient: (clientData) => api.post('/admin/clients', clientData),
  updateClient: (clientId, updates) => api.put(`/admin/clients/${clientId}`, updates),
  deleteClient: (clientId) => api.delete(`/admin/clients/${clientId}`),
  deleteBooking: (bookingId) => api.delete(`/admin/bookings/${bookingId}`),
  exportReport: (params) => api.get('/admin/reports/export', { params, responseType: 'blob' }),
  getClientStats: () => api.get('/admin/client-stats'),
  uploadClientsCSV: (formData) =>
    api.post('/admin/clients/upload-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  uploadBookingsCSV: (formData) =>
    api.post('/admin/bookings/upload-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
};

// Owner API (owner-only, includes financial data)
export const ownerAPI = {
  getCustomers: (params) => api.get('/owner/customers', { params }),
  getCustomer: (id) => api.get(`/owner/customers/${id}`),
  createCustomer: (data) => api.post('/owner/customers', data),
  updateCustomer: (id, data) => api.put(`/owner/customers/${id}`, data),
  deleteCustomer: (id) => api.delete(`/owner/customers/${id}`),
  importCSV: (formData) =>
    api.post('/owner/customers/import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  exportCSV: () => api.get('/owner/customers-export', { responseType: 'blob' }),
  getDashboard: () => api.get('/owner/dashboard'),
  // Nuki Smart Lock
  getNukiLocks: () => api.get('/owner/nuki/locks'),
  getNukiAuths: (lockId) => api.get(`/owner/nuki/locks/${lockId}/auth`),
  getNukiLogs: (lockId, params) => api.get(`/owner/nuki/locks/${lockId}/log`, { params }),
  getNukiDailyStats: (lockId, params) => api.get(`/owner/nuki/daily-stats/${lockId}`, { params }),
  getNukiUserMatches: (lockId) => api.get(`/owner/nuki/user-matches/${lockId}`),
  saveNukiUserMatch: (nukiAuthId, clientId) => api.put(`/owner/nuki/user-matches/${nukiAuthId}`, { client_id: clientId }),
  getNukiLockStudioMappings: () => api.get('/owner/nuki/lock-studio-mappings'),
  saveLockStudioMapping: (lockId, studioId) => api.put(`/owner/nuki/locks/${lockId}/studio`, { studio_id: studioId }),
  // Backups
  getBackups: () => api.get('/owner/backups'),
  createBackup: () => api.post('/owner/backup'),
  downloadBackup: () => api.get('/owner/backup/download'),
  // Website CMS
  getWebsiteImages: (params) => api.get('/owner/website/images', { params }),
  updateWebsiteImage: (id, data) => api.put(`/owner/website/images/${id}`, data),
  uploadWebsiteImage: (id, formData) =>
    api.post(`/owner/website/images/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  removeWebsiteImage: (id) => api.delete(`/owner/website/images/${id}/upload`),
  getWebsiteTexts: (params) => api.get('/owner/website/texts', { params }),
  updateWebsiteText: (id, data) => api.put(`/owner/website/texts/${id}`, data),
  resetWebsiteText: (id) => api.post(`/owner/website/texts/${id}/reset`),
  // Contact Leads
  getLeads: (params) => api.get('/owner/leads', { params }),
  createLead: (data) => api.post('/owner/leads', data),
  sendeLeadErinnerung: () => api.post('/owner/leads/erinnerung', { senden: true }),
  getLeadZustaendige: () => api.get('/owner/leads/zustaendige'),
  updateLead: (id, data) => api.put(`/owner/leads/${id}`, data),
  deleteLead: (id) => api.delete(`/owner/leads/${id}`),
  // Email Campaigns
  getCampaigns: () => api.get('/owner/campaigns'),
  getCampaign: (id) => api.get(`/owner/campaigns/${id}`),
  createCampaign: (data) => api.post('/owner/campaigns', data),
  updateCampaign: (id, data) => api.put(`/owner/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/owner/campaigns/${id}`),
  previewRecipients: (segment) => api.post('/owner/campaigns/preview-recipients', segment),
  sendCampaign: (id) => api.post(`/owner/campaigns/${id}/send`),
  sendTestEmail: (data) => api.post('/owner/campaigns/test-email', data),
  uploadCampaignImage: (formData) =>
    api.post('/owner/campaigns/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  // Newsletter-Abmeldungen
  getUnsubscribes: () => api.get('/owner/newsletter-unsubscribes'),
  addUnsubscribes: (data) => api.post('/owner/newsletter-unsubscribes', data),
  deleteUnsubscribe: (id) => api.delete(`/owner/newsletter-unsubscribes/${id}`),
};

export default api;